import { Injectable, inject } from '@angular/core';
import { 
    DocumentReference, 
    Firestore, 
    collection, 
    doc, 
    query, 
    where, 
    setDoc, 
    serverTimestamp, 
    deleteDoc, 
    writeBatch, 
    orderBy, 
    limit, 
    collectionGroup, 
    onSnapshot,
    QueryConstraint, 
    Query,
    DocumentSnapshot,
    QuerySnapshot,
    WhereFilterOp,
    FieldPath
} from '@angular/fire/firestore';
import { catchError, map, mergeMap, retry, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/auth.service';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { Reference } from './types';

interface UserTeams {
    teams: Reference[];
}

export interface QueryFilter {
    0: string | FieldPath;
    1: WhereFilterOp;
    2: unknown;
}

@Injectable({ providedIn: 'root' })
export class FirestoreService {
    userTeams: Observable<DocumentReference[]>;

    private userDisplayName: { uid: string, name: Promise<string> } = null;

    constructor(private firestore: Firestore = inject(Firestore), private authService: AuthService) {
        // Follows the auth state rather than the uid at construction time: this service is a
        // root singleton, and sign-out is an in-app navigation, so a session that started as
        // one user would otherwise keep watching that user's userTeams document after someone
        // else signs in on the same tab.
        this.userTeams = this.authService.userChanges.pipe(
            switchMap(user => {
                if (!user) {
                    return of([] as DocumentReference[]);
                }
                return this.observeDocument(doc(collection(this.firestore, 'userTeams'), user.uid)).pipe(
                    map(userTeamsChange => {
                        const userTeams = userTeamsChange.data() as UserTeams;
                        return userTeams ? userTeams.teams.map(team => { return team.v }) : [];
                    }),
                    // onSnapshot never resubscribes on its own, so ride out a transient
                    // failure before giving up. Falling back keeps the outer stream alive for
                    // the next sign-in, at the cost of an exhausted error reading as an empty
                    // team list — see the console for the underlying cause.
                    retry({ count: 2, delay: 2000 }),
                    catchError(err => {
                        console.error(err);
                        return of([] as DocumentReference[]);
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }
    
    private observeDocument<T>(ref: DocumentReference): Observable<DocumentSnapshot<T>> {
        return new Observable(subscriber => {
            const unsubscribe = onSnapshot(ref, {
                next: (snap) => subscriber.next(snap as DocumentSnapshot<T>),
                error: (err) => subscriber.error(err)
            });
            return { unsubscribe };
        });
    }

    private observeCollection<T>(queryRef: Query): Observable<QuerySnapshot<T>> {
        return new Observable(subscriber => {
            const unsubscribe = onSnapshot(queryRef, {
                next: (snap) => subscriber.next(snap as QuerySnapshot<T>),
                error: (err) => subscriber.error(err)
            });
            return { unsubscribe };
        });
    }

    // replaceFields: top-level fields of data written as a whole. The merge-set of the other fields merges a map
    // into the document's own, so the keys the new value no longer has would stay in the document.
    async update(collectionId: string, data, parentRefPath?: string, replaceFields?: string[]) {
        try{
            const id = data.id;
            delete data.id;
            delete data.ref;
            data.updated = serverTimestamp();
            data.updatedBy = {
                v: this.getUserReference(),
                d: await this.getUserDisplayName()
            };
            data = await this.replaceReferences(data);
            let docRef = doc(collection(this.firestore, collectionId), id);
            if (parentRefPath) {
                const parentRef = doc(this.firestore, parentRefPath);
                docRef = doc(collection(parentRef, collectionId), id);
            }
            const replaced = (replaceFields || []).filter(field => data[field] !== undefined);
            if (replaced.length > 0) {
                // One atomic write. The merge-set comes first so that a new document exists for the updates,
                // which overwrite the whole value of their field.
                const replacedData = {};
                replaced.forEach(field => {
                    replacedData[field] = data[field];
                    delete data[field];
                });
                const batch = writeBatch(this.firestore);
                batch.set(docRef, data, {merge: true});
                replaced.forEach(field => batch.update(docRef, new FieldPath(field), replacedData[field]));
                await batch.commit();
            }
            else{
                await setDoc(docRef, data, {merge: true});
            }
            // A rename of one's own profile must not keep stamping the previous name.
            if (collectionId === 'users' && this.authService.user && id === this.authService.user.uid) {
                this.clearUserDisplayName();
            }
        }
        catch(err){
            throw err;
        }
    }

    async upsert(collectionId: string, data, parentRefPath?: string, replaceFields?: string[]) {
        data.id = data.id ? data.id : this.getNewDocId();
        if (!data.created) {
            data.created = serverTimestamp();
            data.createdBy = {
                v: this.getUserReference(),
                d: await this.getUserDisplayName()
            };
        }
        else{
            delete data.created;
            if(data.createdBy){
                delete data.createdBy;
            }
        }
        return this.update(collectionId, data, parentRefPath, replaceFields);
    }

    async upsertMultipleDocuments(collectionId: string, dataArr: Array<any>) {
        if (dataArr.length === 0) {
            return;
        }

        try{
            await Promise.all(dataArr.map(async (data) => this.upsert(collectionId, data)));
        }
        catch(err){
            throw err;
        }
    }

    get<T>(collection: string, id: string) {
        return this.getByPath<T>(`/${collection}/${id}`);
    }

    getUserData<T>(collection: string): Observable<T[]> {
        return this.getByPath(`/user${this.capitalize(collection)}/${this.authService.user.uid}`).pipe(
            map((data: any) => {
                if (!data) return [];
                return data[collection].map(row => { return { ...row, path: row.v.path } });
            })
        );
    }

    getUserTeamsData(collection: string) {
        return this.userTeams.pipe(
            mergeMap(userTeams => {
                const observables = userTeams.map(team => {
                    return this.getByPath(`/team${this.capitalize(collection)}/${team.id}`).pipe(
                        map((teamItemsChange: any) => {
                             if (!teamItemsChange) return [];
                            return teamItemsChange[collection].map(row => { return { ...row, path: row.v.path } });
                        })
                    )
                })
                return combineLatest(observables).pipe(
                    map(teamItems => {
                        const flatTeamItems = this.flatten(teamItems) as Reference[];
                        return flatTeamItems.filter((item, index, self) => {
                            const findIndex = self.findIndex(selfItem => {
                                return selfItem.v.id === item.v.id;
                            })
                            return findIndex === index;
                        });
                    })
                )
            }),
            takeUntil(this.authService.initSignOut)
        )
    }

    getByPath<T>(path: string){
        // Updated to use helper
        return this.observeDocument(doc(this.firestore, path)).pipe(
            map(change => {
                if (!change.exists()) {
                    return null;
                }
                const data = change.data();
                return this.getDocument<T>(change.id, data, change.ref);
            }),
            takeUntil(this.authService.initSignOut),
            catchError(err => {
                console.error(err);
                return of(null);
            })
        )
    }

    getFirst<T>(collectionId: string, queryAttributes?: QueryFilter[]) {
        const collectionRef = collection(this.firestore, collectionId);

        if(!queryAttributes){
            queryAttributes = [];
        }
        const queryConstraints: QueryConstraint[] = queryAttributes.map(queryAttribute => {
            return where(queryAttribute[0], queryAttribute[1], queryAttribute[2]);
        });
        queryConstraints.push(orderBy('created'));
        queryConstraints.push(limit(1));

        const collectionQuery = query(collectionRef, ...queryConstraints);

        return this.observeCollection(collectionQuery).pipe(
            map(changes => {
                if (changes.docs.length == 0) { // Note: changes.docs for QuerySnapshot
                    return null;
                }
                const docSnap = changes.docs[0];
                const data = docSnap.data();
                return this.getDocument<T>(docSnap.id, data, docSnap.ref);
            }),
            takeUntil(this.authService.initSignOut),
            catchError(err => {
                console.error(err);
                return of(null);
            })
        )
    }

    getNewDocId() {
        return doc(collection(this.firestore, 'id')).id;
    }

    //returns a list based on the teams of the user
    listUserTeamsData<T>(collectionId: string, type: 'm2m' | 'm2o', queryAttributes?: QueryFilter[]) {
        if (!queryAttributes) {
            queryAttributes = [];
        }
        const hasTeamFilter = queryAttributes.some(queryAttribute => queryAttribute[0] === 'team.v');
        let initialized = false;
        if (type === 'm2o') {
            return this.userTeams.pipe(
                mergeMap(userTeams => {
                    // Firestore rejects an empty `in` array, so a user with no teams — or a
                    // session whose auth state dropped out from under a live subscription —
                    // has to short-circuit rather than build the query.
                    if(!hasTeamFilter && userTeams.length === 0){
                        return of([] as T[]);
                    }
                    //There is a bug for user with more than 10 teams
                    if(!hasTeamFilter){
                        queryAttributes.splice(0, initialized ? 1 : 0, ['team.v', 'in', userTeams]);
                    }
                    initialized = true;
                    return this.list<T>(collectionId, queryAttributes);
                }),
                takeUntil(this.authService.initSignOut)
            );
        }
        return this.userTeams.pipe(
            mergeMap(userTeams => {
                const observables = userTeams.map(team => {
                    const docRef = doc(collection(this.firestore, `team${this.capitalize(collectionId)}`), team.id);
                    return this.observeDocument(docRef).pipe(
                        map(teamItemsChange => {
                            if(!teamItemsChange.exists()){
                                return [] as Reference[];
                            }
                            const teamItems = teamItemsChange.data() as any;
                            return teamItems[Object.keys(teamItems)[0]] as Reference[]; //teamData only has a single field
                        })
                    )
                })
                return combineLatest(observables).pipe(
                    mergeMap(teamItems => {
                        let flatTeamItems = this.flatten(teamItems) as Reference[];
                        flatTeamItems = flatTeamItems.filter((item, index, self) => {
                            const findIndex = self.findIndex(selfItem => {
                                return selfItem.v.id === item.v.id;
                            })
                            return findIndex === index;
                        });
                        const documentObservables = flatTeamItems.map(teamItemRef => {
                            return this.getByPath<T>(teamItemRef.v.path);
                        });
                        return combineLatest(documentObservables).pipe(
                            map(document => {
                                return document;
                            })
                        );
                    })
                )
            }),
            takeUntil(this.authService.initSignOut)
        )
    }

    listUserData<T>(collectionId: string, type: 'm2m' | 'm2o'): Observable<T[]> {
        if (type === 'm2o') {
            return this.list<T>(collectionId, [['user.v', '==', doc(collection(this.firestore,'users'), this.authService.user.uid)]]);
        }
        return this.observeDocument(doc(collection(this.firestore, `user${this.capitalize(collectionId)}`), this.authService.user.uid)).pipe(
            mergeMap(userItemsChange => {
                if (!userItemsChange.exists()) return of([]); // Handle non-existent doc
                
                const userItems = userItemsChange.data() as any;
                const userItemsArray = userItems[collectionId] as Reference[];
                if (!userItemsArray) return of([]);

                const observables = userItemsArray.map(userItems => {
                    return this.getByPath<T>(userItems.v.path);
                })
                return combineLatest(observables).pipe(
                    map(document => {
                        return document;
                    }),
                    takeUntil(this.authService.initSignOut)
                );
            }),
            takeUntil(this.authService.initSignOut)
        )
    }

    list<T>(collectionId: string, queryAttributes?: QueryFilter[], parentRefPath?: string, useCollectionGroup?: Boolean): Observable<T[]>{
        const queryConstraints: QueryConstraint[] = (queryAttributes || []).map(queryAttribute => {
            return where(queryAttribute[0], queryAttribute[1], queryAttribute[2]);
        });

        let collectionRef: Query;
        if (useCollectionGroup) {
            collectionRef = collectionGroup(this.firestore, collectionId);
        }
        else if (parentRefPath) {
            collectionRef = collection(doc(this.firestore, parentRefPath), collectionId);
        }
        else {
            collectionRef = collection(this.firestore, collectionId);
        }

        const collectionQuery = query(collectionRef, ...queryConstraints);

        return this.observeCollection(collectionQuery).pipe(
            map(changes => {
                return changes.docs.map(change => {
                    const data = change.data();
                    return this.getDocument<T>(change.id, data, change.ref);
                });
            }),
            takeUntil(this.authService.initSignOut),
            catchError(err => {
                console.error(err);
                return of([]);
            })
        );
    }

    async deleteDocument(collectionId: string, data) {
        try{
            const docRef = doc(collection(this.firestore, collectionId), data.id);
            await deleteDoc(docRef);
        }
        catch(err){
            throw err;
        }
    }

    deleteMultipleDocuments(collection: string, dataArr: Array<any>) {
        let s = this;
        return new Promise<void>((resolve, reject) => {
            let deleteCount = 0;
            dataArr.forEach(function (data) {
                s.deleteDocument(collection, data).then(res => {
                    deleteCount++
                    if (deleteCount >= dataArr.length)
                        resolve();
                }, err => reject(err));
            });
        });
    }

    getDocumentReference(path: string): DocumentReference {
        return doc(this.firestore, path);
    }

    getUserReference(): DocumentReference {
        return this.getDocumentReference(`users/${this.authService.user.uid}`)
    }

    /**
     * Display name for audit stamps (createdBy.d / updatedBy.d). The Firebase Auth record
     * only carries a displayName for users who went through public sign-up — admin
     * creation, invites and renames in manage/user all write the users document instead —
     * so the profile document is the source of truth here, with Auth as the fallback.
     * Memoized per uid: audit stamps run on every write and must not cost a read each time.
     * A transient failure (rules denial, offline) drops the memo so the next write retries
     * rather than pinning the fallback for the session; a profile that simply carries no
     * name is a settled state and stays memoized. Renames clear it through update().
     */
    private async getUserDisplayName(): Promise<string> {
        const user = this.authService.user;
        if (!user) {
            return '';
        }
        if (!this.userDisplayName || this.userDisplayName.uid !== user.uid) {
            const fallback = (user.displayName || '').trim();
            const name = firstValueFrom(this.getByPath<any>(`/users/${user.uid}`))
                .then(userDoc => {
                    const profileName = userDoc && userDoc.displayName ? String(userDoc.displayName).trim() : '';
                    return profileName || fallback;
                })
                .catch(() => {
                    this.clearUserDisplayName(user.uid);
                    return fallback;
                });
            this.userDisplayName = { uid: user.uid, name: name };
        }
        return this.userDisplayName.name;
    }

    private clearUserDisplayName(uid?: string) {
        if (!uid || (this.userDisplayName && this.userDisplayName.uid === uid)) {
            this.userDisplayName = null;
        }
    }

    private getDocument<T>(id: string, data: any, ref: DocumentReference): T{
        let document = {
            id: id,
            path: ref.path,
            ...data,
            ref: ref
        };
        this.convertToDate(document);

        return document;
    }

    private convertToDate(document: any){
        Object.keys(document).forEach((field) => {
            if (document[field] && typeof document[field].toDate === 'function') {
                document[field] = document[field].toDate()
            }

            if (Array.isArray(document[field])){
                document[field].forEach(item => {
                    if (item && typeof item.toDate === 'function') {
                        item = item.toDate();
                    }
                    else if(typeof item === 'object'){
                        this.convertToDate(item);
                    }
                });
            }
        });

        return document
    }

    private flatten(arr, result = []) {
        for (let i = 0, length = arr.length; i < length; i++) {
            const value = arr[i];
            if (Array.isArray(value)) {
                this.flatten(value, result);
            } else {
                result.push(value);
            }
        }
        return result;
    };

    private getRelCollection(one: string, many: string) {
        return `${this.singular(one)}${this.capitalize(many)}`;
    }

    private singular(str: string) {
        return str.replace(/s$/, '');
    }

    private capitalize(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    private async replaceReferences(data: any){
        if(data && data.path && data.firestore){
            data = await this.getReferenceWithDisplay(data);
        }
        else if(Array.isArray(data)){
            data = await Promise.all(data.map(item => {
                if(item && item.path && item.firestore){
                    return item; // Do not convert array of Document References
                }

                return this.replaceReferences(item);
            }));
        }
        else if(data && typeof data === 'object' && data.constructor.name === 'Object' && !(data.v && data.d)){
            await Promise.all(Object.keys(data).map(async (field) => {
                data[field] = await this.replaceReferences(data[field]);
            }));
        }

        return data;
    }

    private async getReferenceWithDisplay(docRef: DocumentReference){
        const refData = await firstValueFrom(this.getByPath<any>(docRef.path));

        return {
            v: docRef,
            d: refData.displayValue || refData.displayName || refData.name || '',
        };
    }
}